import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Plus, Trash2, GripVertical, Radio } from "lucide-react";

const FREQ_TYPES = ["ATIS", "SIV", "CTR", "AFIS", "A/A", "TMA"];
const NUMERIC = ["altitude", "trackTrue", "distance", "variation"];
const emptyLeg = () => ({ name: "", altitude: 2000, trackTrue: 0, distance: 0, variation: 0, alternate: "", frequencies: [] });

const NumField = ({ label, value, onChange, tid, step }) => (
  <div>
    <Label className="text-[10px] uppercase tracking-wider text-slate-500">{label}</Label>
    <Input type="number" step={step} value={value} onChange={(e) => onChange(e.target.value)} data-testid={tid}
      className="mt-1 h-9 border-slate-700 bg-slate-900/60 font-mono-data text-sm text-slate-100" />
  </div>
);

function Frequencies({ leg, update, p, i }) {
  const freqs = leg.frequencies || [];
  const setFreqs = (fs) => update({ frequencies: fs });
  return (
    <div className="mt-3 rounded-lg border border-slate-800 bg-slate-950/40 p-3">
      <div className="mb-2 flex items-center justify-between">
        <Label className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-cyan-400"><Radio className="h-3 w-3" /> Fréquences radio</Label>
        <button onClick={() => setFreqs([...freqs, { type: "ATIS", name: "", freq: "" }])} data-testid={`${p}add-freq-${i}`}
          className="flex items-center gap-1 rounded-md border border-slate-700 px-2 py-1 text-[11px] text-cyan-300 hover:bg-cyan-500/10">
          <Plus className="h-3 w-3" /> Ajouter
        </button>
      </div>
      {freqs.length === 0 && <p className="text-[11px] text-slate-600">Aucune fréquence. Ajoutez-en (ATIS, SIV, CTR, AFIS, A/A, TMA…).</p>}
      <div className="space-y-2">
        {freqs.map((fr, fi) => {
          const setF = (k, v) => setFreqs(freqs.map((x, y) => (y === fi ? { ...x, [k]: v } : x)));
          return (
            <div key={fi} className="grid grid-cols-12 gap-2" data-testid={`${p}leg-${i}-freq-${fi}`}>
              <select value={fr.type} onChange={(e) => setF("type", e.target.value)} data-testid={`${p}freq-type-${i}-${fi}`}
                className="col-span-3 h-9 rounded-md border border-slate-700 bg-slate-900/60 px-2 text-sm text-slate-100 focus:outline-none">
                {FREQ_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
              <Input value={fr.name || ""} onChange={(e) => setF("name", e.target.value)} placeholder="Dénomination (ex: Paris Info)"
                data-testid={`${p}freq-name-${i}-${fi}`} className="col-span-5 h-9 border-slate-700 bg-slate-900/60 text-sm text-slate-100" />
              <Input value={fr.freq || ""} onChange={(e) => setF("freq", e.target.value)} placeholder="123.450"
                data-testid={`${p}freq-value-${i}-${fi}`} className="col-span-3 h-9 border-slate-700 bg-slate-900/60 font-mono-data text-sm text-slate-100" />
              <button onClick={() => setFreqs(freqs.filter((_, y) => y !== fi))} data-testid={`${p}remove-freq-${i}-${fi}`}
                className="col-span-1 flex items-center justify-center rounded-md text-slate-500 hover:bg-rose-500/10 hover:text-rose-300">
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// p : préfixe des data-testid ("" pour l'aller, "ret-" pour le retour).
export default function LegsEditor({ legs, setLegs, p = "" }) {
  const update = (i, patch) => setLegs((ls) => ls.map((l, x) => (x === i ? { ...l, ...patch } : l)));
  const setLeg = (i, key, v) => update(i, { [key]: NUMERIC.includes(key) ? (v === "" ? "" : Number(v)) : v });
  return (
    <>
      <div className="space-y-3">
        {legs.map((leg, i) => (
          <div key={i} data-testid={`${p}route-leg-${i}`} className="rounded-xl border border-slate-800 bg-slate-900/40 p-4">
            <div className="mb-3 flex items-center gap-2 text-cyan-500/70">
              <GripVertical className="h-4 w-4" />
              <span className="font-mono-data text-xs">BRANCHE {i + 1}</span>
              <button onClick={() => setLegs((ls) => ls.filter((_, x) => x !== i))} data-testid={`${p}remove-leg-${i}`}
                className="ml-auto rounded-lg p-1.5 text-slate-400 hover:bg-rose-500/10 hover:text-rose-300">
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-6">
              <div className="col-span-2">
                <Label className="text-[10px] uppercase tracking-wider text-slate-500">Point d'arrivée</Label>
                <Input value={leg.name} onChange={(e) => setLeg(i, "name", e.target.value)}
                  className="mt-1 h-9 border-slate-700 bg-slate-900/60 text-sm text-slate-100" data-testid={`${p}leg-name-${i}`} />
              </div>
              <NumField label="Alt (ft)" value={leg.altitude} onChange={(v) => setLeg(i, "altitude", v)} tid={`${p}leg-alt-${i}`} />
              <NumField label="Rv (°)" value={leg.trackTrue} onChange={(v) => setLeg(i, "trackTrue", v)} tid={`${p}leg-track-${i}`} />
              <NumField label="Dist (NM)" value={leg.distance} onChange={(v) => setLeg(i, "distance", v)} tid={`${p}leg-dist-${i}`} />
              <NumField label="Décl (° O+)" step="0.1" value={leg.variation} onChange={(v) => setLeg(i, "variation", v)} tid={`${p}leg-var-${i}`} />
            </div>
            <div className="mt-3">
              <Label className="text-[10px] uppercase tracking-wider text-slate-500">Terrain de dégagement (optionnel)</Label>
              <Input value={leg.alternate || ""} onChange={(e) => setLeg(i, "alternate", e.target.value)}
                className="mt-1 h-9 border-slate-700 bg-slate-900/60 text-sm text-slate-100" placeholder="LFXX" />
            </div>
            <Frequencies leg={leg} update={(patch) => update(i, patch)} p={p} i={i} />
          </div>
        ))}
      </div>
      <Button onClick={() => setLegs((ls) => [...ls, { ...emptyLeg(), name: `Point ${ls.length + 1}` }])} variant="outline" size="sm"
        data-testid={`${p}add-leg-button`} className="mt-4 border-slate-700 bg-transparent text-cyan-300 hover:bg-cyan-500/10">
        <Plus className="mr-1 h-3.5 w-3.5" /> Ajouter une branche
      </Button>
    </>
  );
}
